import React, { useEffect, useState } from "react";
import { api, formatDetail, fmtDateTime } from "@/lib/api";
import { Mail, RefreshCw, Check, X, Trash2, AlertCircle, Inbox } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  done: "bg-[#E4F0E8] text-[#18322B]",
  processing: "bg-[#EAF4FA] text-[#1A4971]",
  error: "bg-[#FFE1E1] text-[#8B1E1E]",
  skipped_rejected: "bg-[#F3F3F1] text-[#5C5E60]",
};

const SENDER_COLOR = {
  approved: "bg-[#E4F0E8] text-[#18322B]",
  pending: "bg-[#FFF4E5] text-[#915000]",
  rejected: "bg-[#FFE1E1] text-[#8B1E1E]",
};

export default function EmailImport() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);

  const loadAll = async () => {
    try {
      const [s, l, sd] = await Promise.all([
        api.get("/email-import/status"),
        api.get("/email-import/log", { params: { limit: 100 } }),
        api.get("/email-senders"),
      ]);
      setStatus(s.data);
      setLog(l.data);
      setSenders(sd.data);
    } catch (e) {
      toast.error(formatDetail(e?.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 15000);
    return () => clearInterval(t);
  }, []);

  const pollNow = async () => {
    setPolling(true);
    try {
      const r = await api.post("/email-import/poll-now");
      const s = r.data || {};
      if (s.enabled === false) {
        toast.warning("Email-Import ist deaktiviert (IMAP_ENABLED=false)");
      } else if (s.error) {
        toast.error(s.error);
      } else {
        toast.success(`Poll abgeschlossen — ${s.polled ?? 0} Mails, ${s.done ?? 0} verarbeitet`);
      }
      await loadAll();
    } catch (e) {
      toast.error(formatDetail(e?.response?.data?.detail) || "Poll fehlgeschlagen");
    } finally {
      setPolling(false);
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/email-senders/${id}/approve`);
      toast.success("Absender genehmigt");
      loadAll();
    } catch (e) {
      toast.error(formatDetail(e?.response?.data?.detail) || "Fehlgeschlagen");
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Diesen Absender blockieren? Zukünftige E-Mails werden ignoriert.")) return;
    try {
      await api.post(`/email-senders/${id}/reject`);
      toast.success("Absender blockiert");
      loadAll();
    } catch (e) {
      toast.error(formatDetail(e?.response?.data?.detail) || "Fehlgeschlagen");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Absender entfernen? Beim nächsten Empfang wird er wieder als 'ausstehend' angelegt.")) return;
    try {
      await api.delete(`/email-senders/${id}`);
      toast.success("Entfernt");
      loadAll();
    } catch (e) {
      toast.error(formatDetail(e?.response?.data?.detail) || "Fehlgeschlagen");
    }
  };

  if (loading) return <div className="p-6 text-[#5C5E60]">Lade…</div>;

  const pending = senders.filter((s) => s.status === "pending");
  const approved = senders.filter((s) => s.status === "approved");
  const rejected = senders.filter((s) => s.status === "rejected");

  return (
    <div className="space-y-6" data-testid="email-import-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-[#18322B] tracking-tight">E-Mail-Import</h1>
          <p className="text-sm text-[#5C5E60] mt-1">
            Belege, die an <span className="font-mono">{status?.user || "beleg@vorbuch.de"}</span> gesendet werden,
            werden alle {status?.poll_seconds ?? 180}s automatisch abgeholt.
          </p>
        </div>
        <button
          onClick={pollNow}
          disabled={polling}
          data-testid="btn-poll-now"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#18322B] text-white text-sm hover:bg-[#0f1f1a] disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${polling ? "animate-spin" : ""}`} />
          Jetzt abrufen
        </button>
      </div>

      {/* Status card */}
      <div className="grid grid-cols-4 gap-3">
        <StatusCard label="Status" value={status?.enabled ? "Aktiv" : "Deaktiviert"}
                    tone={status?.enabled ? "ok" : "warn"} icon={Mail} />
        <StatusCard label="Ausstehende Absender" value={pending.length} tone={pending.length > 0 ? "warn" : "muted"} icon={AlertCircle} />
        <StatusCard label="Genehmigte Absender" value={approved.length} tone="ok" icon={Check} />
        <StatusCard label="Letzte Mail" value={fmtDateTime(status?.last_received_at) || "—"} tone="muted" icon={Inbox} />
      </div>

      {!status?.enabled && (
        <div className="rounded-lg border border-[#F0D4A5] bg-[#FFF4E5] p-4 text-sm text-[#915000]">
          <div className="font-semibold mb-1">E-Mail-Import ist deaktiviert.</div>
          Um Belege per E-Mail zu empfangen, setze in <code>backend/.env</code>{" "}
          <code className="font-mono bg-white/60 px-1 rounded">IMAP_ENABLED=true</code> und
          starte den Backend-Service neu. Zugangsdaten sind bereits hinterlegt.
        </div>
      )}

      {/* Pending senders */}
      {pending.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-[#18322B] mb-2">
            Ausstehende Absender ({pending.length})
          </h2>
          <p className="text-xs text-[#5C5E60] mb-3">
            Diese Absender haben zum ersten Mal an Vorbuch geschickt. Bestätige einmalig, dann werden zukünftige Mails ohne Rückfrage übernommen.
          </p>
          <SendersTable list={pending} onApprove={approve} onReject={reject} onDelete={del} />
        </section>
      )}

      {/* Log */}
      <section>
        <h2 className="text-lg font-semibold text-[#18322B] mb-2">Empfangene E-Mails ({log.length})</h2>
        {log.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[#E6E6E4] bg-[#FAFAF8] p-6 text-center text-sm text-[#5C5E60]">
            Noch keine E-Mail empfangen.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-[#E6E6E4] bg-white" data-testid="email-log-table">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F3F3F1] text-[11px] uppercase tracking-wider text-[#5C5E60]">
                <tr>
                  <th className="px-3 py-2 text-left">Empfangen</th>
                  <th className="px-3 py-2 text-left">Absender</th>
                  <th className="px-3 py-2 text-left">Betreff</th>
                  <th className="px-3 py-2 text-center">Anhänge</th>
                  <th className="px-3 py-2 text-center">Belege</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {log.map((row) => (
                  <tr key={row.id} className="border-t border-[#F0F0EE]">
                    <td className="px-3 py-2 whitespace-nowrap text-[#5C5E60]">{fmtDateTime(row.received_at)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.sender}</td>
                    <td className="px-3 py-2">{row.subject || "—"}</td>
                    <td className="px-3 py-2 text-center">{row.attachment_count ?? 0}</td>
                    <td className="px-3 py-2 text-center">{(row.receipt_ids || []).length}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-[11px] ${STATUS_COLOR[row.status] || "bg-[#F3F3F1] text-[#5C5E60]"}`}>
                        {row.status}
                      </span>
                      {row.error && <div className="text-[11px] text-[#8B1E1E] mt-1">{row.error}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Approved / rejected senders */}
      {(approved.length > 0 || rejected.length > 0) && (
        <section>
          <h2 className="text-lg font-semibold text-[#18322B] mb-2">Bekannte Absender</h2>
          <SendersTable list={[...approved, ...rejected]} onApprove={approve} onReject={reject} onDelete={del} />
        </section>
      )}
    </div>
  );
}

function StatusCard({ label, value, tone, icon: Icon }) {
  const toneClass = {
    ok: "bg-[#E4F0E8] text-[#18322B]",
    warn: "bg-[#FFF4E5] text-[#915000]",
    muted: "bg-[#F3F3F1] text-[#5C5E60]",
  }[tone] || "bg-[#F3F3F1] text-[#5C5E60]";
  return (
    <div className="rounded-lg border border-[#E6E6E4] bg-white p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`inline-flex items-center justify-center h-7 w-7 rounded ${toneClass}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="text-[11px] uppercase tracking-wider text-[#5C5E60] font-semibold">{label}</div>
      </div>
      <div className="text-lg font-semibold text-[#18322B]">{value}</div>
    </div>
  );
}

function SendersTable({ list, onApprove, onReject, onDelete }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[#E6E6E4] bg-white" data-testid="email-senders-table">
      <table className="min-w-full text-sm">
        <thead className="bg-[#F3F3F1] text-[11px] uppercase tracking-wider text-[#5C5E60]">
          <tr>
            <th className="px-3 py-2 text-left">Absender</th>
            <th className="px-3 py-2 text-left">Erstmals gesehen</th>
            <th className="px-3 py-2 text-left">Erster Betreff</th>
            <th className="px-3 py-2 text-center">Anz. Mails</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-right">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {list.map((s) => (
            <tr key={s.id} className="border-t border-[#F0F0EE]">
              <td className="px-3 py-2 font-mono text-xs">{s.email}</td>
              <td className="px-3 py-2 text-[#5C5E60] whitespace-nowrap">{fmtDateTime(s.first_seen_at)}</td>
              <td className="px-3 py-2 max-w-md truncate">{s.first_subject || "—"}</td>
              <td className="px-3 py-2 text-center">{s.seen_count ?? 1}</td>
              <td className="px-3 py-2">
                <span className={`inline-block px-2 py-0.5 rounded text-[11px] ${SENDER_COLOR[s.status] || ""}`}>
                  {s.status}
                </span>
              </td>
              <td className="px-3 py-2 text-right">
                <div className="inline-flex gap-1">
                  {s.status !== "approved" && (
                    <button onClick={() => onApprove(s.id)}
                      data-testid={`sender-approve-${s.email}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-[#E4F0E8] text-[#18322B] hover:bg-[#D4E5D8]">
                      <Check className="h-3 w-3" /> Genehmigen
                    </button>
                  )}
                  {s.status !== "rejected" && (
                    <button onClick={() => onReject(s.id)}
                      data-testid={`sender-reject-${s.email}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-[#FFE1E1] text-[#8B1E1E] hover:bg-[#FBD1D1]">
                      <X className="h-3 w-3" /> Blockieren
                    </button>
                  )}
                  <button onClick={() => onDelete(s.id)}
                    data-testid={`sender-delete-${s.email}`}
                    className="inline-flex items-center px-2 py-1 rounded text-xs bg-[#F3F3F1] text-[#5C5E60] hover:bg-[#E6E6E4]">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
